import React from 'react';
import { Card, CardBody, CardHeader, Spinner } from 'reactstrap';
import { useTranslator } from '@front-activerse/common-components';
import type { WearableSleepSummary } from '@front-activerse/common-components';
import type { ApolloError } from '@apollo/client';
import type { DateRange } from '../types';
import { toChartSeries } from '../helpers/series';
import MetricValue from './MetricValue';
import MetricChart from './MetricChart';
import ProviderDeviceChip from './ProviderDeviceChip';

interface SleepMetric {
  key: keyof WearableSleepSummary;
  labelKey: string;
  unitKey?: string;
}

const SLEEP_METRICS: SleepMetric[] = [
  {
    key: 'durationMinutes',
    labelKey: 'wearablesDashboard.metric.sleepDuration',
    unitKey: 'wearablesDashboard.unit.minutes',
  },
  {
    key: 'timeInBedMinutes',
    labelKey: 'wearablesDashboard.metric.timeInBed',
    unitKey: 'wearablesDashboard.unit.minutes',
  },
  {
    key: 'efficiencyPercent',
    labelKey: 'wearablesDashboard.metric.efficiency',
    unitKey: 'wearablesDashboard.unit.percent',
  },
  {
    key: 'awakeMinutes',
    labelKey: 'wearablesDashboard.metric.awake',
    unitKey: 'wearablesDashboard.unit.minutes',
  },
  {
    key: 'lightMinutes',
    labelKey: 'wearablesDashboard.metric.lightSleep',
    unitKey: 'wearablesDashboard.unit.minutes',
  },
  {
    key: 'deepMinutes',
    labelKey: 'wearablesDashboard.metric.deepSleep',
    unitKey: 'wearablesDashboard.unit.minutes',
  },
  {
    key: 'remMinutes',
    labelKey: 'wearablesDashboard.metric.remSleep',
    unitKey: 'wearablesDashboard.unit.minutes',
  },
  {
    key: 'interruptionsCount',
    labelKey: 'wearablesDashboard.metric.interruptions',
  },
  { key: 'napCount', labelKey: 'wearablesDashboard.metric.naps' },
  {
    key: 'napDurationMinutes',
    labelKey: 'wearablesDashboard.metric.napDuration',
    unitKey: 'wearablesDashboard.unit.minutes',
  },
  {
    key: 'avgHeartRateBpm',
    labelKey: 'wearablesDashboard.metric.sleepHrAvg',
    unitKey: 'wearablesDashboard.unit.bpm',
  },
  {
    key: 'avgHrvSdnnMs',
    labelKey: 'wearablesDashboard.metric.hrvAvg',
    unitKey: 'wearablesDashboard.unit.ms',
  },
  {
    key: 'avgRespiratoryRate',
    labelKey: 'wearablesDashboard.metric.respiratoryRate',
    unitKey: 'wearablesDashboard.unit.breathsPerMinute',
  },
  {
    key: 'avgSpo2Percent',
    labelKey: 'wearablesDashboard.metric.spo2',
    unitKey: 'wearablesDashboard.unit.percent',
  },
];

interface Props {
  range: DateRange;
  loading: boolean;
  error: ApolloError | undefined;
  sleepSummary: WearableSleepSummary[];
}

const SleepSummarySection: React.FC<Props> = ({ range, loading, error, sleepSummary }) => {
  const { t } = useTranslator();
  const dayRow = sleepSummary[sleepSummary.length - 1] ?? null;

  return (
    <Card className="mb-4" data-testid="sleep-summary-section">
      <CardHeader>{t('wearablesDashboard.section.sleep.title')}</CardHeader>
      <CardBody>
        {loading && !dayRow ? <Spinner /> : null}
        {error ? (
          <div data-testid="sleep-summary-error" className="text-danger">
            {t('wearablesDashboard.error.section')}: {error.message}
          </div>
        ) : null}
        {!loading && !error && sleepSummary.length === 0 ? (
          <div data-testid="sleep-summary-empty">
            {range.isSingleDay
              ? t('wearablesDashboard.empty.day')
              : t('wearablesDashboard.empty.range')}
          </div>
        ) : null}
        {range.isSingleDay && dayRow
          ? SLEEP_METRICS.map((metric) => (
              <MetricValue
                key={metric.key as string}
                labelKey={metric.labelKey}
                unitKey={metric.unitKey}
                value={dayRow[metric.key] as number | null}
                provider={dayRow.provider}
                device={dayRow.device}
              />
            ))
          : null}
        {!range.isSingleDay && sleepSummary.length > 0 ? (
          <>
            {(() => {
              const lastWithProvider = [...sleepSummary].reverse().find((row) => row.provider);
              return lastWithProvider ? (
                <div className="mb-3">
                  <ProviderDeviceChip
                    provider={lastWithProvider.provider}
                    device={lastWithProvider.device}
                  />
                </div>
              ) : null;
            })()}
            {SLEEP_METRICS.map((metric) => (
              <div key={metric.key as string} className="mb-4">
                <small className="text-muted d-block mb-1">{t(metric.labelKey)}</small>
                <MetricChart
                  points={toChartSeries(
                    sleepSummary,
                    range,
                    (row) => row[metric.key] as number | null
                  )}
                  ariaLabel={t(metric.labelKey)}
                  unit={metric.unitKey ? t(metric.unitKey) : undefined}
                />
              </div>
            ))}
          </>
        ) : null}
      </CardBody>
    </Card>
  );
};

export default SleepSummarySection;
export { SLEEP_METRICS };
